import axios from 'axios';
import Auth from '@aws-amplify/auth';

const states = {
  attemptSSO: {
    title: 'Sign in',
    subtitle: 'Please enter your username',
    usernameEnabled: true,
    submitButtonText: 'Next',
    cancelButtonText: 'Cancel SSO redirect...',
    redirectTimer: null,
  },

  login: {
    title: 'Sign in',
    subtitle: 'Please enter your username and password',
    usernameEnabled: false,
    passwordEnabled: true,
    forgotPasswordEnabled: true,
    backToLoginEnabled: true,
    submitButtonText: 'Sign in',
    usernameError: '',
    passwordError: '',
  },
  customChallenge: {
    title: 'One time password',
    subtitle: 'Please enter your one time password',
    usernameEnabled: false,
    passwordEnabled: true,
    forgotPasswordEnabled: true,
    backToLoginEnabled: true,
    submitButtonText: 'Sign in',
    usernameError: '',
    passwordError: '',
  },
  reset: {
    title: 'Reset password',
    subTitle: 'Please enter a new password',
    passwordEnabled: true,
    usernameEnabled: false,
    confirmPasswordEnabled: true,
    forgotPasswordEnabled: true,
    backToLoginEnabled: true,
    submitButtonText: 'Reset password',
    resetError: '',
    usernameError: '',
    passwordError: '',
  },

  recover: {
    title: 'Password recovery',
    usernameEnabled: true,
    subtitle: 'A password reset code will be sent to your email',
    submitButtonText: 'RESET PASSWORD',
    backToLoginEnabled: true,
    usernameError: '',
    passwordError: '',
  },
  resetRequired: {
    title: 'Password reset',
    usernameEnabled: false,
    subtitle: 'Please enter your recovery code',
    codeEnabled: true,
    passwordEnabled: true,
    confirmPasswordEnabled: true,
    forgotPasswordEnabled: true,
    submitButtonText: 'Press here to change your password',
    backToLoginEnabled: true,
    usernameError: '',
    passwordError: '',
  },
  forceNewPassword: {
    title: 'Password change',
    subtitle: 'Please enter a new password',
    usernameEnabled: false,
    passwordEnabled: true,
    confirmPasswordEnabled: true,
    forgotPasswordEnabled: false,
    submitButtonText: 'Set new password',
    backToLoginEnabled: true,
    resetError: '',
    usernameError: '',
    passwordError: '',
    passwordLabel: 'New password',
    confirmPasswordLabel: 'Confirm new password',
  },
  loginSuccess: {
    submitFormDisabled: true,
    message: 'Login successful!',
  },

  final: {
    submitFormDisabled: true,
  },
};
const DEFAULT_OPTS = {
  auth: Auth,
  loginTargetProvider: {
    async getTarget(username) {
      const response = await axios.get(`${process.env.VUE_APP_BASE_API_URL}/api/login/${username}`);
      return response.data || {
        loginMode: 'password',
      };
    },
  },
  onSignInSuccess() {
    window.location.href = process.env.VUE_APP_BASE_URL;
  },
};

const phonePattern = /^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s./0-9]*$/;
function isPhoneNr(username) {
  return phonePattern.test(username) && username.length >= 7;
}
export default class LoginState {
  constructor(authOptions) {
    this.config = (authOptions || DEFAULT_OPTS);
    this.setMode('attemptSSO');
  }

  setSignInSuccessListener(listener) {
    this.config.onSignInSuccess = listener;
  }

  setMode(mode, errorText, mergeState = null) {
    this.mode = mode;
    this.form = {
      ...states[mode],
      ...mergeState,
    };
    this.form.errorText = (errorText || '');
    return this.form;
  }

  getMode() {
    return this.mode;
  }

  setFinalState(success, message, type) {
    this.setMode('final');
    this.form.success = success;
    this.form.message = message;
    this.form.type = type || '';
  }

  startRecovery() {
    return this.setMode('recover');
  }

  async checkForSSORedirect(ssoProvider) {
    if (ssoProvider) {
      await this.config.auth.federatedSignIn({ provider: ssoProvider });
    } else {
      const usernameFromLocalStorage = localStorage.getItem('ssoUsername');

      if (usernameFromLocalStorage) {
        this.form.title = 'SSO Sign In';
        this.form.subtitle = 'You will be redirected automatically';
        this.form.ssoRedirect = true;

        let timeLeft = 5;
        const subTitle = this.form.subtitle;
        this.form.subtitle = `${subTitle} in ${timeLeft}s`;

        this.form.redirectTimer = setInterval(() => {
          if (timeLeft === 1) {
            clearInterval(this.form.redirectTimer);
            this.attemptSSO(usernameFromLocalStorage);
          } else {
            timeLeft -= 1;
            this.form.subtitle = `${subTitle} in ${timeLeft}s`;
          }
        }, 1000);
      }
    }
    return this.form;
  }

  cancelRedirectTimer() {
    clearInterval(this.form.redirectTimer);
    localStorage.removeItem('ssoUsername');

    this.form.title = 'Sign in';
    this.form.subtitle = 'Please enter your username';
    this.form.ssoRedirect = false;

    return this.form;
  }

  async customAuth(username) {
    this.user = await this.config.auth.signIn(username);
  }

  async onSubmit(username, password, code) {
    await this[this.mode](username, password, code);
    return this.form;
  }

  async attemptSSO(username) {
    try {
      if (isPhoneNr(username)) {
        await this.login(username);
      } else {
        const loginConfig = await this.config.loginTargetProvider.getTarget(username);

        if (loginConfig.loginMode === 'password') {
          this.setMode('login');
        } else if (loginConfig.loginMode === 'reset') {
          this.setMode('resetRequired');
        } else if (loginConfig.loginMode === 'SSO') {
          localStorage.setItem('ssoUsername', username);
          await this.config.auth.federatedSignIn({
            provider: loginConfig.idpName,
          });
        } else {
          console.error(`Unknown loginMode:${loginConfig.loginMode}`);
        }
      }
    } catch (err) {
      this.setMode('login');
    }
  }

  async login(username, password) {
    try {
      this.user = await this.config.auth.signIn(username.trim(), password ? password.trim() : password);
      if (this.user) {
        if (this.user.challengeName === 'CUSTOM_CHALLENGE') {
          this.setMode('customChallenge');
        } else if (this.user.challengeName === 'NEW_PASSWORD_REQUIRED') {
          this.setMode('forceNewPassword');
        } else {
          this.setMode('loginSuccess');
          if (this.config.onSignInSuccess) {
            this.config.onSignInSuccess(this.user);
          }
        }
      }
    } catch (error) {
      switch (error.code) {
        case 'UserNotConfirmedException':
          // The error happens if the user didn't finish the confirmation step when signing up
          // In this case you need to resend the code and confirm the user
          // About how to resend the code and confirm the user, please check the signUp part
          this.form.usernameError = error.message;
          break;
        case 'PasswordResetRequiredException':
          // The error happens when the password is reset in the Cognito console
          // In this case you need to call forgotPassword to reset the password
          // Please check the Forgot Password part.
          this.setMode('resetRequired', error.message);
          break;
        case 'NotAuthorizedException':
          // The error happens when the incorrect password is provided
          this.form.passwordError = error.message;
          break;
        case 'UserNotFoundException':
          // The error happens when the supplied username/email does not exist in the Cognito user pool
          this.form.usernameError = 'Incorrect username.';
          break;
        default:
          this.form.usernameError = 'System error';
      }
    }
  }

  async customChallenge(user, password) {
    try {
      this.user = await Auth.sendCustomChallengeAnswer(this.user, password);
      this.onSignInSuccess(this.user);
    } catch (err) {
      this.setMode('login', err.message);
    }
  }

  async forceNewPassword(user, newPassword) {
    try {
      const userResponse = await this.config.auth.completeNewPassword(this.user, newPassword.trim());
      this.onSignInSuccess(userResponse);
    } catch (err) {
      this.setMode('forceNewPassword', err.message, { keepPassword: true });
    }
  }

  onSignInSuccess(user) {
    if (this.config.onSignInSuccess) {
      this.config.onSignInSuccess(user);
    }
  }

  async resetRequired(username, newPassword, code) {
    try {
      await this.config.auth.forgotPasswordSubmit(username, code, newPassword);
      await this.login(username, newPassword);
    } catch (err) {
      this.setFinalState(0, err.message);
    }
  }

  async getUser() {
    try {
      const user = await this.config.auth.currentAuthenticatedUser();
      if (user && user.signInUserSession) {
        return user;
      }
      return null;
    } catch (err) {
      return null;
    }
  }

  // static async redirectAfterLogin(user) {
  //   const decodedToken = await user.signInUserSession.idToken.decodePayload();
  // }

  async recover(username) {
    try {
      let isUserConfirmed;
      const body = { userId: username };
      const res = await axios.post(`${process.env.VUE_APP_BASE_API_URL}/api/login/user/forgotPassword`, body);
      if (res && res.data) {
        isUserConfirmed = res.data.result === 'VERIFICATION_CODE_SENT';
      }

      if (isUserConfirmed) {
        this.setMode('resetRequired');
      } else {
        this.setFinalState(1, 'Check your email we sent you with temporary password.', 'temp-password');
      }
    } catch (err) {
      this.setFinalState(0, 'We could not reset your password. Please contact your administrator or try again later.');
    }
  }

  signOut() {
    return this.config.auth.signOut()
      .then((data) => data)
      .catch((err) => err);
  }

  async useAuthGuard(router) {
    router.beforeResolve(async (to, from, next) => {
      const user = await this.getUser();
      if (user) {
        if (process.env.VUE_APP_NAME === 'login') {
          this.onSignInSuccess(user);
        } // on router init if we are in login app and logged in redirect
        if (to.matched.some((record) => typeof (record.meta.requiresAuth) === 'boolean' && !record.meta.requiresAuth)) {
          return next();
        }
      } else if (to.matched.some((record) => record.meta.requiresAuth)) {
        window.location.href = `${process.env.VUE_APP_BASE_URL}login/`;
        return true;
      }
      return next();
    });
    setInterval(() => {
      this.getUser();
    }, 1000 * 60 * 5);
  }

  // login: { submitBtn: 'Log in' },
  // reset: { submitBtn: 'Reset password' },
  // recovery: { submitBtn: 'Reset my password' },
  // sso_attempt: { submitBtn: 'Next' },
  // force_new_password: { submitBtn: 'Set new password' },
}
